import { useCallback, useEffect, useRef, useState } from 'react';
import { deleteAnnotation, loadAnnotations, writeAnnotation } from '../../app/annotations';
import { setTileOverride } from '../../app/lockedBoard';
import { log } from '../../app/logger';
import { recorder } from '../../app/recorder';
import type { AnnotationRow, HypothesisPhase, MatchRow, Status, StatusOverride } from '../../types';
import { useRecorderView } from '../../ui/capture/useRecorder';
import { PhaseIcon } from '../PhaseIcon';
import { phaseLabelB, statusLabelB } from '../phases';
import { annotationTextB, OVERRIDE_CHOICES, voiceNoteAvailableB } from './detailSheetB';

/**
 * B-15 — the **hypothesis detail sheet** (design spec §2.4; scenarios 10 and 17): what a
 * long-press on any Discovery row or board tile opens, and the workflow's Step-1 annotate entry
 * point. One component, three entry points, because there is one sheet.
 *
 * §2.4's contents, in order: hypothesis text + phase, count + status, **Annotate** (free text,
 * `WriteAnnotation`; dictation is the iOS keyboard's mic, not ours), **Hold to talk** voice note
 * (`recorder.holdVoiceNote`), status override buttons (`SetStatusOverride` — reversible, count
 * untouched), and the existing annotations listed.
 *
 * ── A `<dialog>`, deliberately NON-modal (the B-12 shape) ───────────────────
 *
 * `showModal()` would inert the transition control, the flag button and the undo pill. A dead
 * spell ends when the game says so, and the whistle may never be fenced behind a reading sheet —
 * so this uses `show()` and positions itself over the middle band, exactly as the set-piece sheet
 * does and for the same reason. The explicit Done is the one way out (a non-modal dialog fires no
 * `cancel`).
 *
 * ── Typing here is allowed, and it is never required ────────────────────────
 *
 * LIVE-9 forbids *requiring* free-text entry in play, not offering it (`commands/annotations.ts`
 * carries the same standing ruling). Nothing on any capture surface routes through this sheet;
 * it is opened by a deliberate 450 ms hold, and closing it without typing costs nothing.
 *
 * ── What this sheet does NOT offer ──────────────────────────────────────────
 *
 * **Renaming.** There is no hypothesis-text-edit command in the engine and none is wanted
 * (ruled 2026-08-14) — annotations are the per-pattern note. Nothing here implies otherwise.
 */

/** The rendered pattern, as its host surface already holds it — this sheet re-reads no counts. */
export interface SheetHypothesisB {
  readonly id: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly cachedCount: number;
  readonly status: Status;
  readonly statusOverride: StatusOverride;
}

export interface HypothesisSheetBProps {
  /** The SHELL's authoritative match row (B-10's one-authority rule). */
  readonly match: MatchRow;
  readonly hypothesis: SheetHypothesisB;
  /** An override committed — the host re-reads (its own rows carry the status it renders). */
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

const MODULE = 'ui-b/hypothesisSheet';

export function HypothesisSheetB({ match, hypothesis, onChanged, onClose }: HypothesisSheetBProps) {
  const dialog = useRef<HTMLDialogElement | null>(null);
  const view = useRecorderView();
  const [notes, setNotes] = useState<readonly AnnotationRow[] | null>(null);
  const [draft, setDraft] = useState('');
  /** The annotation being edited, or `null` while the field is composing a new one (INV-14's arm). */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [override, setOverride] = useState<StatusOverride>(hypothesis.statusOverride);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Synchronous in-flight guard — `busy` only disables a control after React commits (U-7). */
  const working = useRef(false);

  useEffect(() => {
    const element = dialog.current;
    if (element === null || element.open) return;
    element.show();
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    const rows = await loadAnnotations(match.id, hypothesis.id);
    setNotes(rows);
  }, [match.id, hypothesis.id]);

  useEffect(() => {
    reload().catch((cause: unknown) => {
      const message = cause instanceof Error ? cause.message : String(cause);
      setNotice(message);
      log.error(MODULE, 'the annotations could not be read', { message });
      setNotes([]);
    });
  }, [reload]);

  /**
   * Every write on this sheet takes the same route: one dispatch, a rejection rendered as an
   * ordinary sentence (never the fatal banner), and a re-read on success — the imperative
   * re-read convention. Serialized on a ref so a double-tapped Save cannot dispatch twice.
   */
  const run = useCallback(
    (
      what: string,
      act: () => Promise<{ readonly ok: boolean; readonly message?: string }>,
      after: () => void,
    ): void => {
      if (working.current) return;
      working.current = true;
      setBusy(true);
      setNotice(null);
      void act().then(
        (result) => {
          working.current = false;
          setBusy(false);
          if (!result.ok) {
            setNotice(result.message ?? 'That could not be saved.');
            return;
          }
          after();
        },
        (cause: unknown) => {
          working.current = false;
          setBusy(false);
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, `${what} failed`, { message });
        },
      );
    },
    [],
  );

  const text = annotationTextB(draft);

  const saveAnnotation = (): void => {
    if (text === null) return;
    run(
      'an annotation save',
      () =>
        writeAnnotation({
          id: editingId ?? undefined,
          matchId: match.id,
          hypothesisId: hypothesis.id,
          text,
        }),
      () => {
        setDraft('');
        setEditingId(null);
        void reload();
      },
    );
  };

  const removeAnnotation = (annotationId: string): void => {
    run('an annotation delete', () => deleteAnnotation(match.id, annotationId), () => {
      if (editingId === annotationId) {
        setEditingId(null);
        setDraft('');
      }
      void reload();
    });
  };

  const applyOverride = (next: StatusOverride): void => {
    run('a status override', () => setTileOverride(match.id, hypothesis.id, next), () => {
      // The count is untouched by construction — `SetStatusOverride`'s op set cannot compile a
      // count write (scenario 10's negative is a type fact, not a promise kept here).
      setOverride(next);
      onChanged();
    });
  };

  const recordingThis =
    view.recording !== null &&
    view.recording.subject.kind === 'voiceNote' &&
    view.recording.subject.hypothesisId === hypothesis.id;

  const toggleVoiceNote = (): void => {
    setNotice(null);
    void recorder.holdVoiceNote(match, hypothesis.id).then(
      (action) => {
        if (action.kind === 'refused') {
          setNotice('Nothing was recorded — the microphone is unavailable. Type the note instead.');
        }
      },
      (cause: unknown) => {
        const message = cause instanceof Error ? cause.message : String(cause);
        setNotice(message);
        log.error(MODULE, 'the voice note control failed', { message });
      },
    );
  };

  return (
    <dialog
      ref={dialog}
      className="hypb"
      aria-labelledby="hypb-title"
      data-testid="hypb"
      data-override={override ?? 'none'}
      data-notes={notes === null ? 'loading' : String(notes.length)}
    >
      <header className="hypb-bar">
        <h2 className="hypb-title" id="hypb-title">
          Pattern
        </h2>
        <button type="button" className="hypb-done" data-testid="hypb-done" onClick={onClose}>
          Done
        </button>
      </header>

      <div className="hypb-body">
        <section className="hypb-head">
          <p className="hypb-text" data-testid="hypb-text">
            {hypothesis.text}
          </p>
          <div className="hypb-meta">
            <span className="hypb-phase" data-testid="hypb-phase">
              <PhaseIcon phase={hypothesis.phase} />
              {phaseLabelB(hypothesis.phase)}
            </span>
            <span className="hypb-count" data-testid="hypb-count">
              {hypothesis.cachedCount}
              <span className="hypb-count__unit"> observed</span>
            </span>
            <span className="hypb-status" data-status={hypothesis.status} data-testid="hypb-status">
              {statusLabelB(hypothesis.status)}
            </span>
          </div>
        </section>

        {notice !== null && (
          <p className="hypb-notice" role="status" data-testid="hypb-notice">
            {notice}
          </p>
        )}

        {/* ── Annotate (scenario 17) ──────────────────────────────────────── */}
        <section className="hypb-card">
          <h3 className="hypb-card__title">
            <label htmlFor="hypb-annotate">
              {editingId === null ? 'Annotate' : 'Edit this note'}
            </label>
          </h3>
          <p className="hypb-card__note">
            Optional. It travels with this pattern into Coach Claude — “applies to #7; the sub who
            plays there doesn’t”.
          </p>
          <textarea
            id="hypb-annotate"
            className="hypb-field"
            data-testid="hypb-annotate"
            rows={3}
            value={draft}
            placeholder="Type or dictate a note about this pattern."
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <div className="hypb-row">
            <button
              type="button"
              className="hypb-primary"
              data-testid="hypb-annotate-save"
              disabled={text === null || busy}
              onClick={saveAnnotation}
            >
              {editingId === null ? 'Save note' : 'Save edit'}
            </button>
            {editingId !== null && (
              <button
                type="button"
                className="hypb-secondary"
                data-testid="hypb-annotate-cancel"
                onClick={() => {
                  setEditingId(null);
                  setDraft('');
                }}
              >
                Cancel edit
              </button>
            )}
            {voiceNoteAvailableB(match) && (
              <button
                type="button"
                className="hypb-secondary"
                data-testid="hypb-voice"
                data-recording={recordingThis ? 'true' : 'false'}
                onClick={toggleVoiceNote}
              >
                {recordingThis ? 'Stop recording' : 'Record a voice note'}
              </button>
            )}
          </div>

          {notes !== null && notes.length > 0 && (
            <ul className="hypb-notes" data-testid="hypb-notes">
              {notes.map((note) => (
                <li className="hypb-note" key={note.id} data-testid={`hypb-note-${note.id}`}>
                  <p className="hypb-note__text">{note.text}</p>
                  <div className="hypb-note__actions">
                    <button
                      type="button"
                      className="hypb-linkish"
                      data-testid={`hypb-note-edit-${note.id}`}
                      onClick={() => {
                        setEditingId(note.id);
                        setDraft(note.text);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="hypb-linkish"
                      data-testid={`hypb-note-remove-${note.id}`}
                      disabled={busy}
                      onClick={() => {
                        removeAnnotation(note.id);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── Status override (scenario 10 — reversible; the count never moves) ─ */}
        <section className="hypb-card">
          <h3 className="hypb-card__title">Status</h3>
          <div className="hypb-overrides" role="group" aria-label="Status override">
            {OVERRIDE_CHOICES.map((choice) => (
              <button
                key={choice.key}
                type="button"
                className="hypb-override"
                data-testid={`hypb-override-${choice.key}`}
                aria-pressed={override === choice.value}
                disabled={busy || (choice.value === null && override === null)}
                onClick={() => {
                  applyOverride(choice.value);
                }}
              >
                <span className="hypb-override__label">{choice.label}</span>
                <span className="hypb-override__hint">{choice.hint}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </dialog>
  );
}
